import { Component, OnInit, ViewChild } from '@angular/core';
import { ColDef } from 'ag-grid-enterprise';
import { AgGridTableComponent } from 'src/app/common/ag-grid-table/ag-grid-table.component';
import { DataService } from 'src/app/services/data.service';
import { ActivityLogEntityTypeOption, ActivityTrackerService } from './activity-tracker.service';
import { ActivityLogEntityLinkCellRendererComponent } from './activity-log-entity-link-cell-renderer/activity-log-entity-link-cell-renderer.component';

interface UserOption {
  _id: string;
  name?: string;
  email?: string;
}

const ACTION_OPTIONS = [
  { label: 'Create', value: 'create' },
  { label: 'Update', value: 'update' },
  { label: 'Delete', value: 'delete' },
];

@Component({
  selector: 'app-activity-tracker',
  templateUrl: './activity-tracker.component.html',
  styleUrls: ['./activity-tracker.component.sass'],
})
export class ActivityTrackerComponent implements OnInit {
  @ViewChild(AgGridTableComponent) gridTable: AgGridTableComponent;

  public readonly entity = 'activity-logs';
  public columnDefs: ColDef[] = [];
  public tableParams: any = { hideAdd: true, hideHeader: true, defaultQuery: {} };

  public readonly actionOptions = ACTION_OPTIONS;
  public entityTypeOptions: ActivityLogEntityTypeOption[] = [];
  /** controllerName -> apiControllerRoute, mutated in place so the already-registered cell renderer always reads the latest map. */
  public readonly entityRouteMap: Record<string, string> = {};

  public selectedUser: UserOption | null = null;
  public userSuggestions: UserOption[] = [];
  /** Holds a controllerName (e.g. "ItemController") — the CASL subject, and the query filter key. */
  public selectedEntityType: string | null = null;
  public selectedAction: string | null = null;
  public dateRange: Date[] | null = null;

  constructor(
    private dataService: DataService,
    private activityTrackerService: ActivityTrackerService,
  ) {}

  async ngOnInit(): Promise<void> {
    // Built only once entity types are in hand: `<app-ag-grid-table>` clones
    // colDefs (e.g. when merging in a saved column preset), which would
    // otherwise detach the entity-link renderer's routeMap from this map —
    // any update after that clone would go unseen.
    try {
      this.entityTypeOptions = await this.activityTrackerService.getEntityTypeOptions();
      this.entityTypeOptions.forEach((option) => {
        this.entityRouteMap[option.value] = option.apiControllerRoute;
      });
    } catch {
      this.entityTypeOptions = [];
    }

    this.columnDefs = this.buildColumnDefs();
  }

  private buildColumnDefs(): ColDef[] {
    return [
      {
        // The actor is denormalized directly onto each row (userName/userEmail) rather than
        // populated — a 50-row feed must not trigger 50 User lookups.
        headerName: 'User',
        field: 'userName',
        valueGetter: (params) => params.data?.userName || params.data?.userEmail || '—',
        flex: 1,
        resizable: true,
      },
      {
        headerName: 'Action',
        field: 'action',
        valueFormatter: (params) => this.capitalize(params.value),
        flex: 1,
        resizable: true,
      },
      {
        // entityType is already a display label set server-side (simplifyControllerName), not a
        // raw value that needs a client-side lookup.
        headerName: 'Entity Type',
        field: 'entityType',
        valueFormatter: (params) => params.value || '—',
        flex: 1,
        resizable: true,
      },
      {
        headerName: 'Entity',
        field: 'entityName',
        cellRenderer: ActivityLogEntityLinkCellRendererComponent,
        cellRendererParams: { routeMap: this.entityRouteMap },
        flex: 2,
        resizable: true,
      },
      {
        headerName: 'Timestamp',
        field: 'timestamp',
        valueFormatter: (params) => (params.value ? new Date(params.value).toLocaleString() : ''),
        sort: 'desc',
        flex: 1,
        resizable: true,
      },
    ];
  }

  private capitalize(value: string): string {
    return value ? value.charAt(0).toUpperCase() + value.slice(1) : '';
  }

  async searchUsers(event: { query: string }): Promise<void> {
    const query = event.query?.trim();

    try {
      const results = await this.dataService.getAllOfTypeAsync('users', {
        query: query
          ? { $or: [{ name: { $regex: query, $options: 'i' } }, { email: { $regex: query, $options: 'i' } }] }
          : {},
        select: 'name email id _id',
        sort: { name: 1 },
        limit: 10,
        autopopulate: false,
      });
      this.userSuggestions = Array.isArray(results) ? results : [];
    } catch {
      this.userSuggestions = [];
    }
  }

  applyFilters(): void {
    this.tableParams.defaultQuery = this.buildDefaultQuery();
    this.gridTable?.api?.refreshServerSide?.({ purge: true });
  }

  clearFilters(): void {
    this.selectedUser = null;
    this.selectedEntityType = null;
    this.selectedAction = null;
    this.dateRange = null;
    this.applyFilters();
  }

  get hasFilters(): boolean {
    return !!(this.selectedUser || this.selectedEntityType || this.selectedAction || this.dateRange?.length);
  }

  private buildDefaultQuery(): Record<string, any> {
    const query: Record<string, any> = {};

    if (this.selectedUser?._id) {
      query.user = this.selectedUser._id;
    }
    if (this.selectedEntityType) {
      query.controllerName = this.selectedEntityType;
    }
    if (this.selectedAction) {
      query.action = this.selectedAction;
    }

    const [start, end] = this.dateRange || [];
    if (start || end) {
      const range: Record<string, Date> = {};
      if (start) {
        range.$gte = start;
      }
      if (end) {
        const endOfDay = new Date(end);
        endOfDay.setHours(23, 59, 59, 999);
        range.$lte = endOfDay;
      }
      query.timestamp = range;
    }

    return query;
  }
}
